import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import styles from './MeterUploadCalendar.styles';
import {
  WEEKDAYS,
  COLORS,
  firstDayIndexUTC,
  daysInUtcMonth,
  isoForUTCDate,
  getColorForDay,
  getTitleForDay,
} from '../../utils/meterDetails.helpers';

export default function MeterUploadCalendar({
  statusByDate = {},
  viewDate,
  setViewDate,
  selectedDate,
  onDayClick,
}) {
  const theme = useTheme();
  useMediaQuery(theme.breakpoints.down('sm'));

  const rootRef = useRef(null);
  const headerRef = useRef(null);
  const legendRef = useRef(null);
  const weekRef = useRef(null);

  const [rowPx, setRowPx] = useState(38);

  const year = viewDate.getUTCFullYear();
  const month = viewDate.getUTCMonth();

  const monthName = viewDate.toLocaleString('default', { month: 'short', year: 'numeric' });
  const firstDayIndex = firstDayIndexUTC(year, month);
  const daysInMonth = daysInUtcMonth(year, month);
  const todayIso = new Date().toISOString().slice(0, 10);

  const totalCells = firstDayIndex + daysInMonth;
  const weekRows = Math.min(6, Math.max(4, Math.ceil(totalCells / 7)));

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => {
      const H = el.clientHeight;
      const headerH = headerRef.current?.offsetHeight ?? 0;
      const legendH = legendRef.current?.offsetHeight ?? 0;
      const weekH = weekRef.current?.offsetHeight ?? 0;
      const rowGap = 8;
      const totalGaps = rowGap * Math.max(0, weekRows - 1);
      const available = Math.max(0, H - headerH - legendH - weekH - totalGaps - 8);
      const perRow = Math.max(26, Math.floor(available / weekRows));
      setRowPx(perRow);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [weekRows]);

  const dotPx = useMemo(() => {
    const max = Math.floor(rowPx * 0.72);
    return Math.max(18, Math.min(max, 34));
  }, [rowPx]);

  const prevMonth = () => { onDayClick?.(null); setViewDate(new Date(Date.UTC(year, month - 1, 1))); };
  const nextMonth = () => { onDayClick?.(null); setViewDate(new Date(Date.UTC(year, month + 1, 1))); };

  const handleDayClick = (iso) => { if (iso <= todayIso) onDayClick?.(iso); };

  return (
    <Box ref={rootRef} sx={styles.calendarSection}>
      <Box ref={headerRef} sx={styles.calendarHeaderBar}>
        <IconButton size="small" aria-label="Previous month" onClick={prevMonth}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={styles.calendarMonthLabel}>{monthName}</Typography>
        <IconButton size="small" aria-label="Next month" onClick={nextMonth}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box ref={legendRef} sx={styles.calendarLegend}>
        <Box sx={styles.calendarLegendItem}>
          <Box sx={styles.calendarLegendDot(COLORS.exported)} />
          <Typography variant="caption">Exported</Typography>
        </Box>
        <Box sx={styles.calendarLegendItem}>
          <Box sx={styles.calendarLegendDot(COLORS.loaded)} />
          <Typography variant="caption">Loaded</Typography>
        </Box>
        <Box sx={styles.calendarLegendItem}>
          <Box sx={styles.calendarLegendDot(COLORS.empty)} />
          <Typography variant="caption">No Data</Typography>
        </Box>
        <Box sx={styles.calendarLegendItem}>
          <Box sx={styles.calendarLegendDot(COLORS.today)} />
          <Typography variant="caption">Today</Typography>
        </Box>
      </Box>

      <Box ref={weekRef} sx={styles.calendarWeekHeader}>
        {WEEKDAYS.map((d) => (
          <Typography key={d} sx={styles.calendarWeekday}>{d}</Typography>
        ))}
      </Box>

      <Box sx={{ ...styles.calendarDaysGrid, gridTemplateRows: `repeat(${weekRows}, ${rowPx}px)` }}>
        {Array.from({ length: firstDayIndex }).map((_, i) => (
          <Box key={`pad-${i}`} sx={styles.calendarPadCell} />
        ))}

        {Array.from({ length: daysInMonth }).map((_, idx) => {
          const day = idx + 1;
          const iso = isoForUTCDate(year, month, day);
          const isSelected = iso === selectedDate;
          const bg = getColorForDay(iso, todayIso, statusByDate, theme);
          const title = getTitleForDay(iso, todayIso, statusByDate);

          const onClick = () => handleDayClick(iso);
          const onKeyDown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } };

          return (
            <Tooltip title={title} arrow key={iso} disableInteractive>
              <Box
                role="button"
                tabIndex={0}
                aria-label={`${day} ${title}`}
                onClick={onClick}
                onKeyDown={onKeyDown}
                sx={styles.calendarDayCell}
              >
                <Box sx={styles.calendarDayDot(bg, isSelected, theme, iso, todayIso, dotPx)}>
                  <Typography sx={styles.calendarDayText(dotPx)}>{day}</Typography>
                </Box>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}
